import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import MuniSelectionV2 from './MuniSelectionV2'; // Ensure correct import path
import Button from '@mui/material/Button';
import {useUser} from '../UserContext'
import Container from '@mui/material/Container';
import AnalysisAccordion from './AnalysisAccordion';
import StableMuiDialog from '../../common/StableMuiDialog.js';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { toast } from '../../common/toast';
import axiosInstance, { mlAxiosInstanceNoToast, showRequestFailureToast } from '../../fetchConfig/axiosConfig'
import { usePersistedDataStore, useCompStore, useLocalDataStore} from '../store';
import CircularProgress from '@mui/material/CircularProgress';
import { isEqual } from 'lodash';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import { useNYData } from '../../common/NyDataObject';
import { getDefaultYear } from '../../common/helper_functions';
import AdvancedSettings, { loadAdvancedSettingsFromStorage } from './AdvancedSettings';
import { getDefaultCompLimitForReportType } from './SaveComps/createSaveObject';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import NegotiatorAnalysisRebuilt from './ResultAnalysis/NegotiatorAnalysisRebuilt';
import { Navigate } from 'react-router-dom';
import InfoIcon from '@mui/icons-material/Info';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import { AVAILABLE_BAD_DATA_FIELDS, columnHeaderMappings, getBadDataParams } from '../../common/mappings.js';
import BadDataFlagsSelector from './DataAnalysis/BadDataFlagsSelector';
import logger from '../../utils/logger';
// import TasksDataGrid from './TasksDataGrid';
import NewTaskGrid from './TaskGrid/NewTaskGrid.js';
import { processFilterDataToSettings } from '../../common/dateUtils';
import CompSearch from './CompSearch';
import { initializeFetch, buildRunParams } from '../../services/initializeFetchUtils';
import { isCaseSettled } from './SummaryCaseTable/utils/helpers';
import { invokeModels, invokeFilingGeneration } from '../../services/InvokeModels';
import { useStreamProcessorWorker } from '../../hooks/useStreamProcessorWorker';
import { CCalendar } from '@propriety/court-calendar';
import '@propriety/court-calendar/styles.css';


// Wrapper component to filter out MUI props that shouldn't be on DOM elements (causing console errors)
const TooltipWrapper = React.forwardRef(({ children, ...other }, ref) => {
  // Filter out MUI-specific props that shouldn't be on DOM elements
  const {
    fullWidth,
    indicator,
    selectionFollowsFocus,
    textColor,
    ...domProps
  } = other;
  
  return (
    <Box
      ref={ref}
      component="span"
      sx={{ display: 'inline-flex' }}
      {...domProps}
    >
      {children}
    </Box>
  );
});

TooltipWrapper.displayName = 'TooltipWrapper';

const COURT_CALENDAR_API_KEY = 'kCivvw9jBjdFconLv01tgoAkGNXHPysMEjhBLOGJoc1vySgIzFaMDtF9RTq28d75wc8RErD8YZShfZMckB3lsHjV9ox933lskX3iMi3HO5G6H1sgYsNZKoZKpXi0SwUZ1txhMhVEe3tsxfHs0rX0CU7UMDQuPOHv1WaKECxF6hlqulCmUNcafwPkdxSUuTp6jTr9m62wQr0R9YLud8nv15CBIwg86SYRv9r2qqgqTqvHm0nx6a4msQWXxsGcaD2n';
const ANALYSIS_BOTTOM_PANEL_SX = { display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, mt: 0, mb: 0 };
const ANALYSIS_BOTTOM_TABS_SX = {
  mt: 0,
  mb: 0,
  maxHeight: '30px',
  py: 0,
  minHeight: '30px',
  flexShrink: 0,
  '& .MuiTab-root': {
    minHeight: '30px',
    padding: '4px 12px',
    textTransform: 'none',
  },
};
const ANALYSIS_BOTTOM_CONTENT_SX = { flex: 1, minHeight: 0, minWidth: 0, overflow: 'auto' };
const UNASSIGNED_COURT_DATE = 'unassigned';

// CCalendar is a plain function component; memo stops CaseAnalysisContainer store updates from rebuilding FullCalendar.
const MemoCCalendar = React.memo(CCalendar);

const AnalysisBottomPanel = React.memo(function AnalysisBottomPanel({ activeUser, isAdmin, canCreateTasks }) {
  const [tasksTabValue, setTasksTabValue] = useState(() => {
    const param = new URLSearchParams(window.location.search).get('tab');
    if (param === 'tasks' || param === '1') return 1;
    if (param === 'results' || param === '2') return 2;
    return 0;
  });
  const handleTabChange = useCallback((_event, newValue) => {
    setTasksTabValue(newValue);
  }, []);

  return (
    <Box sx={ANALYSIS_BOTTOM_PANEL_SX}>
      <Tabs
        value={isAdmin ? tasksTabValue : Math.min(tasksTabValue, 1)}
        onChange={handleTabChange}
        sx={ANALYSIS_BOTTOM_TABS_SX}
      >
        <Tab label="Calendar" />
        <Tab label="Tasks" />
        {isAdmin && <Tab label="Results" />}
      </Tabs>
      <Box sx={ANALYSIS_BOTTOM_CONTENT_SX}>
        {tasksTabValue === 0 && <MemoCCalendar apiKey={COURT_CALENDAR_API_KEY} activeUser={activeUser} />}
        {tasksTabValue === 1 && <NewTaskGrid isAdmin={canCreateTasks} />}
        {tasksTabValue === 2 && isAdmin && <NegotiatorAnalysisRebuilt userId={activeUser} />}
      </Box>
    </Box>
  );
});

// if this doesn't update on comp location change you have succeeded.
// this is getting a bit bloated with the addition of additional settings etc.

// FOR THE OPTIMIZATIONS, follow these practices. https://medium.com/@sagarpatil23399/efficient-react-you-might-not-need-useeffect-95decbf2dbae

  // Utility function to reorder columns based on preferred order - preferred order should look like this:
  // ['parcel_id', 'Address', ...]
  export const reorderColumns = (columns, preferredOrder) => {
    return columns.sort((a, b) => {
      const aIndex = preferredOrder.indexOf(a.field);
      const bIndex = preferredOrder.indexOf(b.field);
      
      // If both columns are in preferred order, sort by their order
      if (aIndex !== -1 && bIndex !== -1) {
        return aIndex - bIndex;
      }
      // If only a is in preferred order, it comes first
      if (aIndex !== -1) return -1;
      // If only b is in preferred order, it comes first
      if (bIndex !== -1) return 1;
      // If neither is in preferred order, maintain original order
      return 0;
    });
  };

// THis whole component is gross - the location params and state for params causes unnecessary double renders.
function CaseAnalysisContainer({courtDates, globalCompRef, compStreamObject}) {
  const [restrictedYear, setRestrictedYear] = useState(null);
  const [runType, setRunType] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('runType') || 'all_clients';
  });
  const getReportType = usePersistedDataStore((state) => state.reportType);
  const setReportType = usePersistedDataStore((state) => state.setReportType);
  const [propertyType, setPropertyType] = useState('residential');
  const [expandedAccordion, setExpandedAccordion] = useState(null);
  
  // the same way you abstracted the additionalInput, you should abstract the courtDate to location params.
  const [courtDate, setCourtDate] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('courtDate') || null;
  });
  const [courtDateOptions, setCourtDateOptions] = useState([]);
  const [selectedCourtDate, setSelectedCourtDate] = useState(null);
  const [unassignedOptions, setUnassignedOptions] = useState([]);
  const [selectedUnassignedKey, setSelectedUnassignedKey] = useState('');
  const [unassignedLoading, setUnassignedLoading] = useState(false);
  const [repID, setRepID] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('repID') || '';
  });
  const [activeDialog, setActiveDialog] = useState(null);
  const [error, setError] = useState({ county: null, municipality: null, village: null, repID: null });
  const { user, canRunReport, canUseRunType, canUseFeature } = useUser();
  const shareWorkups =
    user?.isLinkAuthenticated === true && Boolean(user?.shareWorkups);
  // We don't have this funcitonality to export for whatever reason, but this works for now.
  const isAdmin = user.userAccess.level === 'admin' || user.userAccess.level === 'propadmin';
  
  const canCreateTasks = canUseFeature('task_creation');
  const setIsFetching = usePersistedDataStore((state) => state.setIsFetching);
  const { processStreamWithWorker, resetProcessingState } = useStreamProcessorWorker();
  const getIsFetching = usePersistedDataStore((state) => state.isFetching);
  const [reviewId, setReviewId] = useState(null);
  const setReviewPage = useLocalDataStore((state)=>state.setReviewPage)
  const setCompSheet = usePersistedDataStore((state) => state.setCompSheet);
  const setOptimizedComps = usePersistedDataStore((state) => state.setOptimizedComps);
  const setNegotiationObj = usePersistedDataStore((state) => state.setNegotiationObj);
  const setSummaryCaseTableSortModel = usePersistedDataStore((state) => state.setSummaryCaseTableSortModel);
  const getAdjustments = useCompStore((state) => state.adjustments);
  const setAdjustments = useCompStore((state) => state.setAdjustments);
  const [dataGridRows, setDataGridRows] = useState([]);
  const setMarketingResponse = usePersistedDataStore((state) => state.setMarketingResponse);
  const setTotalCases = usePersistedDataStore((state) => state.setTotalCases);
  const [advancedSettings, setAdvancedSettings] = useState(loadAdvancedSettingsFromStorage);

  const currentFilter = advancedSettings.currentFilter || null;
  // should the user overwrite manually saved comps? - now derived from advanced settings
  let overwriteComps = advancedSettings?.manualReviewOverride === 1;
  const [settlementData, setSettlementData] = useState(null);
  const setDataAnalysisResult = usePersistedDataStore((state) => state.setDataAnalysisResult);

  // Use persisted state for bad data flags
  const badDataFlags = usePersistedDataStore((state) => state.badDataFlags);
  const setBadDataFlags = usePersistedDataStore((state) => state.setBadDataFlags);
  const flagType = usePersistedDataStore((state) => state.flagType);

  // Add filter state and fetching logic
  const [filters, setFilters] = useState({ admin_filters: [], user_filters: [] });
  const [isLoadingFilters, setIsLoadingFilters] = useState(false);

  // Initialize location params once during component mount
  const [locationParams, setLocationParams] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const initialParams = {
      state: params.get('state') || 'NY',
      county: params.get('county') || 'All',
      municipality: params.get('municipality') || '',
      village: params.get('village') || '',
      TaxYear: parseInt(params.get('TaxYear') || '') || getDefaultYear(),
      MuniCode: params.get('MuniCode') || 'All',
      municipalityString: params.get('municipalityString'),
      reportType: params.get('reportType') || 'standard',
      runType: params.get('runType') || 'all_clients',
      additionalInput: params.get('additionalInput') || ''  // Add additionalInput to locationParams
    };

    // Handle settlement report type initialization
    if (initialParams.reportType === 'settlement') {
      initialParams.county = 'All';
      initialParams.municipality = 'All';
      initialParams.village = 'All';
      initialParams.MuniCode = 'All';
    }

    return initialParams;
  });

  // Sync URL reportType into the store after mount — never call store setters inside useState init
  // (that updates ResponsiveAppBar while CaseAnalysisContainer is still rendering).
  useEffect(() => {
    setReportType(locationParams.reportType || 'standard');
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Track previous municipality code to detect changes. Start null so the first render
  // still applies getBadDataParams(MuniCode); otherwise prev === current and we skip county
  // defaults whenever store badDataFlags !== county template (e.g. S04 omits Bath vs store default).
  const prevMuniCodeRef = useRef(null);
  
  // Memoized county-specific bad data flags
  const countySpecificBadDataFlags = useMemo(() => {
    if (!locationParams?.MuniCode) return badDataFlags;
    
    const countySpecificParams = getBadDataParams(locationParams.MuniCode);
    const muniCodeChanged = prevMuniCodeRef.current !== locationParams.MuniCode;
    
    // Treat "defaults" as this muni's county template (not a hardcoded list — templates omit fields like TotalGarageCap)
    const isDefaultDataFlags = isEqual(badDataFlags?.data || [], countySpecificParams?.data || []);
    const isDefaultOutlierFlags = isEqual(badDataFlags?.outliers || [], countySpecificParams?.outliers || []);
    
    // Apply county-specific defaults if:
    // 1. Municipality code has changed (reset to new defaults), OR
    // 2. User is using default store values (initial load)
    if (muniCodeChanged || (isDefaultDataFlags && isDefaultOutlierFlags)) {
      // Update the ref to track the current muni code
      prevMuniCodeRef.current = locationParams.MuniCode;
      
      return countySpecificParams;
    }
    
    // User has made custom selections and muni hasn't changed, don't override them
    return badDataFlags;
  }, [locationParams?.MuniCode, badDataFlags]);

  // Update store when county-specific flags change
  useEffect(() => {
    if (!isEqual(countySpecificBadDataFlags, badDataFlags)) {
      setBadDataFlags(countySpecificBadDataFlags);
    }
  }, [countySpecificBadDataFlags, badDataFlags, setBadDataFlags]);

  // // Returning to the analysis page should never leave Run in a loading state from a prior session.
  // useEffect(() => {
  //   setIsFetching(false);
  //   resetProcessingState();
  // }, [setIsFetching, resetProcessingState]);

  // Getting new filters may not be properly fetching if user saves new filters (check flow)
  const fetchFilters = useCallback(async () => {
    // Reset sort model when fetching filters (user is on analysis page)
    setSummaryCaseTableSortModel([]);
    
    if (!user?.userId) {
      logger.warn('No user ID available for fetching filters');
      return;
    }
    setIsLoadingFilters(true);
    try {
      const response = await axiosInstance.post('/load-categorized-filters', {
        UserId: user?.userId
      });
      if (response.data && response.data.categorized_filters) {
        setFilters(prevFilters => {
          if (isEqual(prevFilters, response.data.categorized_filters)) {
            return prevFilters;
          }
          return response.data.categorized_filters;
        });
        
        // Sync advancedSettings if needed
        const currentFilter = advancedSettings.currentFilter;
        const allFilters = [
          ...response.data.categorized_filters.admin_filters,
          ...response.data.categorized_filters.user_filters
        ];
        const matchedFilter = allFilters.find(f => f.FilterName === currentFilter);
        
        if (matchedFilter) {
          // Use the utility function to process filter data
          // Note: evalDate will now come from advancedSettings
          const processedSettings = processFilterDataToSettings(matchedFilter, advancedSettings.evalDate);

          if (processedSettings) {
            // Compare current settings with filter settings
            const settingsDiffer = 
              (advancedSettings.squareFeetFilter) !== processedSettings.squareFeetFilter ||
              (advancedSettings.acreageFilter) !== processedSettings.acreageFilter ||
              (advancedSettings.grossAdjPctFilter) !== processedSettings.grossAdjPctFilter ||
              (advancedSettings.netAdjPctFilter) !== processedSettings.netAdjPctFilter ||
              JSON.stringify(advancedSettings.compFilters || []) !== JSON.stringify(processedSettings.compFilters) ||
              (advancedSettings.squareFeetFilterEnabled ?? true) !== processedSettings.squareFeetFilterEnabled ||
              (advancedSettings.acreageFilterEnabled ?? true) !== processedSettings.acreageFilterEnabled ||
              (advancedSettings.grossAdjPctFilterEnabled ?? true) !== processedSettings.grossAdjPctFilterEnabled ||
              (advancedSettings.netAdjPctFilterEnabled ?? true) !== processedSettings.netAdjPctFilterEnabled
            
            if (settingsDiffer) {
              const newSettings = {
                ...advancedSettings,
                ...processedSettings
              };
              setAdvancedSettings(newSettings);
              localStorage.setItem('advancedSettings', JSON.stringify(newSettings));
            } else {
              logger.log('Settings do not differ, skipping update');
            }
          }
        }
          
      }
    } catch (error) {
      logger.error('Error fetching filters:', error);
    } finally {
      setIsLoadingFilters(false);
    }
  }, [user, advancedSettings, setAdvancedSettings]);

  useEffect(() => {
    fetchFilters();
  }, [user]);

  // ALL the advanced settings params should be set at runtime of intialization function to set these states to prevent stale state issues.

  // Dynamically derive flags based on defaultRunTypes in settings
  const showSettledCases = advancedSettings?.defaultRunTypes?.includes('settled');
  const scarOnly = advancedSettings?.defaultRunTypes?.includes('scarFiled') || false;
  // Derive manualReview from autosave setting - when autosave is true, manualReview should be false
  const manualReview = advancedSettings?.autosave === false;
  // Same source as negotiation mlsCompsOnly (see initializeFetchUtils buildRunParams)
  const mlsCompsOnly = advancedSettings?.compFilters?.includes('mlsOnly') || false;
  // i dont think allow conditions issues is used anywhere. Its default should also probably be false.
  const allowConditionIssues = advancedSettings?.compFilters?.includes('allowConditionIssues') || true;
  const reviewedCasesFlag = advancedSettings?.defaultRunTypes?.includes('reviewed') || false;
  const reportTypeRestricted = getReportType === 'settlement' || getReportType === 'data_analysis' || getReportType === 'marketing';

  const isCompLimitExplicitlySet = useCallback((compLimit, reportType) => {
    if (compLimit === null || compLimit === undefined || compLimit === '') return false;
    return compLimit !== getDefaultCompLimitForReportType(reportType);
  }, []);

  // Check if current settings differ from defaults to display the "!" icon.
  const hasNonDefaultFilters = useMemo(() => {
    const checks = [
      advancedSettings?.autosave === false, // Show "!" when autosave is OFF (manual review is true)
      advancedSettings?.unreviewed_flag !== 'False',
      (advancedSettings?.defaultRunTypes?.filter(type => type !== 'settled') || []).length > 0,
      !!(advancedSettings?.currentFilter && advancedSettings.currentFilter !== '' && advancedSettings.currentFilter !== 'Default'),
      // Only show comp limit as non-default if autosave is enabled and comp limit is explicitly set (not the default for report type)
      advancedSettings?.autosave === true && isCompLimitExplicitlySet(advancedSettings?.compLimit, getReportType)
    ];
    return checks.some(Boolean);
  }, [advancedSettings, getReportType, isCompLimitExplicitlySet]);

  // Check if manual review override is enabled to display red warning icon
  const hasOverrideEnabled = useMemo(() => {
    return advancedSettings?.manualReviewOverride === 1;
  }, [advancedSettings]);

  const hasHighTaxRun = useMemo(() => {
    return advancedSettings?.highTaxRun === true;
  }, [advancedSettings]);

  const hasTimeAdjustmentEnabled = useMemo(() => {
    const n = Math.floor(Number(advancedSettings?.timeAdjustmentMonths));
    return Number.isFinite(n) && n >= 1;
  }, [advancedSettings]);

  const hasAlgorithmStageWarning = useMemo(() => {
    const stage = advancedSettings?.stage;
    if (stage === 'BAR') return true;
    return typeof stage === 'string' && stage.toLowerCase() === 'marketing';
  }, [advancedSettings]);




  // Consolidate location param updates with URL updates
  const updateLocationParams = useCallback((updates) => {
    setLocationParams(prev => {
      const nextParams = { 
        ...prev, 
        ...updates,
        municipality: updates.county ? (updates.municipality || 'All') : (updates.municipality || prev.municipality)
      };

      // Only update if there are actual changes
      if (!isEqual(prev, nextParams)) {
        // Update URL params in the same operation
        const searchParams = new URLSearchParams(window.location.search);
        Object.entries(nextParams).forEach(([key, value]) => {
          if (value) {
            searchParams.set(key, value);
          } else {
            searchParams.delete(key);
          }
        });
        
        // Update URL without triggering navigation
        const newUrl = `${window.location.pathname}?${searchParams.toString()}`;
        window.history.replaceState(null, '', newUrl);
        
        return nextParams;
      }
      return prev;
    });
  }, []);

  const { getCounties, getMunicipalities, getVillages, getLocationNameFromMuniCode } = useNYData();
  const { state, county, municipality, village, TaxYear } = locationParams;
  // this is old think you can just use the new lookup in nydataobj for these. / alter it to work for that
  const counties = getCounties();
  const countyLookup = useMemo(
    () => Object.fromEntries(counties.map(({ key, selectVal }) => [selectVal, key])),
    [counties]
  );
  const municipalities = useMemo(() => {
    if (county) {
      // Use selectedCounty for dynamic lookup
      return getMunicipalities(countyLookup[county]) || [];
    }
    // Fallback to county if no selection
    return county ? getMunicipalities(countyLookup[county]) : [];
  }, [county, getMunicipalities, countyLookup]);
  
  // AdvancedSettings persists to localStorage and syncs negotiationObj before calling onConfirm.
  const handleSettingsConfirm = useCallback((newSettings) => {
    setAdvancedSettings(newSettings);
    useLocalDataStore.getState().setSettingsUpdated(true);
  }, []);

  const handleRunTypeChange = (event) => {
    const newRunType = event.target.value;
    
    // Clear additional input and update URL
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.delete('additionalInput');
    searchParams.delete('courtDate');
    if (newRunType !== 'rep_id') searchParams.delete('repID');
    searchParams.set('runType', newRunType);  
    
    // Update URL without these params
    const newUrl = `${window.location.pathname}?${searchParams.toString()}`;
    window.history.replaceState(null, '', newUrl);
    
    // Clear states only if they have values
    if (courtDate !== null) setCourtDate(null);
    if (selectedCourtDate !== null) setSelectedCourtDate(null);
    if (courtDateOptions.length > 0) setCourtDateOptions([]);
    if (newRunType !== 'rep_id' && repID !== '') setRepID('');
    
    // Reset restricted year if switching away from court_date run type
    if (newRunType !== 'court_date' && restrictedYear !== null) {
      setRestrictedYear(null);
    }
    
    if (runType === 'court_date' && error.courtDate) {
      setError((prev) => ({
        ...prev,
        courtDate: null,
      }));
    }

    if ((runType === 'single_pid') && error.additionalInput) {
      setError((prev) => ({
        ...prev,
        additionalInput: null,
      }));
    }

    // Update both state and locationParams, clearing additionalInput
    setRunType(newRunType);
    updateLocationParams({ 
      runType: newRunType,
      additionalInput: '' // Clear additionalInput when run type changes
    });
  };

  const clearError = useCallback(
    (key) => {
      if(error[key] && error[key]!==null){
      setError((prev) => ({
        ...prev,
        [key]: null,
      }));
    }
    },
    [error]
  );

  const handleActiveDialog = (value) => setActiveDialog(value);
  const handleDialogClose = () => {
    setActiveDialog(null);
  };


  


  

  // Add state to control navigation
  const [shouldNavigate, setShouldNavigate] = useState(false);

  // Memoize the current muniCode and tax year
  const currentMuniCode = locationParams.MuniCode;
  const currentTaxYear = useMemo(() => locationParams.TaxYear || (new Date().getFullYear() - 1), [locationParams.TaxYear]);


  // Load adjustments only when muniCode changes (using useEffect, not useMemo)
  // CHANGE THIS 
  useEffect(() => {
    if (
      !getAdjustments ||
      getAdjustments.countycode !== currentMuniCode
    ) {
      if (currentMuniCode && currentMuniCode !== 'All') {
        mlAxiosInstanceNoToast
          .post('/v1/data/load_adjustments', { MuniCode: currentMuniCode })
          .then((response) => {
            logger.log('[Adjustment Loader] load_adjustments data:', response.data);
            const adj = response.data?.response;
            if (adj) {
              setAdjustments({ ...adj, muniCode: currentMuniCode, taxYear: currentTaxYear });
            }
          })
          .catch((error) => {
            logger.error('[Adjustment Loader] Error loading adjustments:', error);
            showRequestFailureToast(error, { context: 'load adjustments' });
          });
      }
    }
  }, [currentMuniCode, currentTaxYear, getAdjustments, setAdjustments]);

  // Reset settlement rounding when muniCode changes (similar to adjustment load)
  const settlementRoundingMuniCode = usePersistedDataStore((state) => state.settlementRoundingMuniCode);
  const setSettlementRoundingMuniCode = usePersistedDataStore((state) => state.setSettlementRoundingMuniCode);
  const setSettlementRounding = usePersistedDataStore((state) => state.setSettlementRounding);
  const getNegotiationObj = usePersistedDataStore((state) => state.negotiationObj);
  
  // UPDATE THIS SO THAT IT LOADS IN BASED ON WHAT THE ROUNDING SHOULD BE FOR THAT MUNICIPALITY
  useMemo(() => {
    if (currentMuniCode && currentMuniCode !== 'All' && settlementRoundingMuniCode !== currentMuniCode) {
      // Check if there are any settled cases in the current municipality
      const hasSettledCases = getNegotiationObj?.cases?.some(caseItem => isCaseSettled(caseItem));
      
      if (hasSettledCases) {
        // Preserve existing rounding parameter for municipalities with settled cases
        logger.log('[Settlement Rounding] Preserving rounding parameter for muniCode with settled cases:', currentMuniCode);
      } else {
        // Reset settlement rounding to default when muniCode changes and no settled cases
        setSettlementRounding(0);
        logger.log('[Settlement Rounding] Reset to default for new muniCode:', currentMuniCode);
      }
      
      setSettlementRoundingMuniCode(currentMuniCode);
    }
  }, [currentMuniCode, settlementRoundingMuniCode, setSettlementRounding, setSettlementRoundingMuniCode, getNegotiationObj]);

  // Handler for saving adjustments (from AdjustmentsDialog)
  const handleSaveAdjustments = (newAdjustments) => {
    setAdjustments({ ...newAdjustments, muniCode: currentMuniCode, taxYear: currentTaxYear });
    toast.success('Adjustments saved!');
  };

  // State to hold the number of clients for large query dialog
  const [largeQueryCount, setLargeQueryCount] = useState(0);
  const [largeQueryParams, setLargeQueryParams] = useState(null);

  // --- Parameter Validation Helper ---
  /**
   * Validates location and report type selection for analysis run.
   * Returns an object with error fields if invalid, or null if valid.
   */
  function validateParameters({ locationParams, getReportType, runType, courtDate }) {
    const errors = {};

    // Special validation for evidence_comparison - only check county and municipality
    if (getReportType === 'evidence_comparison') {
      if (locationParams.county === 'All' || !locationParams.county || locationParams.county === '') {
        errors.county = 'Select a county.';
      }
      if (locationParams.municipality === 'All' || !locationParams.municipality || locationParams.municipality === '') {
        errors.municipality = 'Select a municipality.';
      }
      // Return early - no other validations for evidence_comparison
      return Object.keys(errors).length > 0 ? errors : null;
    }

    if (!locationParams.TaxYear && getReportType !== 'settlement') {
      errors.TaxYear = 'Select a tax year.';
    }
    switch (getReportType) {
      case 'data_analysis':
        if (!locationParams.county || locationParams.county === '') {
          errors.county = 'Select a county.';
        }
        break;
      case 'settlement':
        // No location validation needed for settlement reports
        break;
      default:
        if (!locationParams.county || locationParams.county === '') {
          errors.county = 'Select a county.';
        } else if (locationParams.county !== 'All' && !locationParams.municipality) {
          errors.municipality = 'Select a municipality.';
        }
        break;
    }
    if (runType === 'court_date' && !courtDate) {
      errors.courtDate = 'Select a court date.';
    }
    if ((runType === 'single_parcel') && !locationParams.additionalInput) {
      const errorString = 'Parcel ID or Address';
      errors.additionalInput = `Enter a valid ${errorString}`;
    }
    if (runType === 'rep_id' && (!repID || repID === '')) {
      errors.repID = 'Enter a Rep ID.';
    }
    return Object.keys(errors).length > 0 ? errors : null;
  }

  // --- Run Size & Warning Validation Helper ---
  /**
   * Checks for manual review, large query, and related warnings.
   * Returns a string (dialog type) if a warning should be shown, or null if not.
   */
  function validateRunSizeAndWarnings({
    force,
    manualReview,
    isTrainingUser,
    getReportType,
    runType,
    locationParams,
    skipLargeQueryWarning,
    overwriteComps
  }) {
    // Skip warnings for evidence_comparison report type
    if (getReportType === 'evidence_comparison') {
      return null;
    }

    // Manual review override warning - check if override is enabled and user is about to run analysis
    if (
      !force &&
      overwriteComps === true &&
      getReportType !== 'settlement' &&
      getReportType !== 'data_analysis'
    ) {
      return 'manualReviewOverrideWarning';
    }

    // Large run warning logic (example: full county run)
    if (
      !force &&
      !skipLargeQueryWarning &&
      runType === 'all_clients' &&
      getReportType !== 'settlement' &&
      getReportType !== 'data_analysis' &&
      (locationParams.county === 'All' || locationParams.municipality === 'All')
    ) {
      return 'largeQueryWarning';
    }
    return null;
  }

  // --- Dialog Helper ---
  /**
   * Shows a dialog. Note: We no longer store params here since we use current state on re-run.
   * This ensures we always have the most recent locationParams, MuniCode, etc. when confirming dialogs.
   * @param {string} dialogType - The dialog to show (e.g., 'largeQueryWarning')
   * @param {object} params - Params (kept for backward compatibility, but not stored)
   * @param {function} setActiveDialog - State setter for dialog
   * @param {function} setLargeQueryParams - State setter (kept for backward compatibility, but not used)
   */
  function showDialog(dialogType, params, setActiveDialog, setLargeQueryParams) {
    setActiveDialog(dialogType);
    // Note: We don't store params anymore - we use current state when re-running
    // This ensures we always have the most recent locationParams, MuniCode, etc.
  }

  const reviewTypeToReportType = {
    DataCleaning: 'data_analysis',
    CaseReview: 'standard',
    Negotiation: 'standard', // Negotiation tasks run the same analysis as Case Review
    Settled: 'settlement',
  };

  // Unified analysis flow that handles all report types consistently
  const executeAnalysis = useCallback(async (params) => {
    try {
      const {
        accordionId = null,
        force = false,
        overwriteComps: overwriteOverride = overwriteComps,
        skipLargeQueryWarning = false,
        additionalInput,
        uids = null,
        MuniCode: overrideMuniCode,
        TaxYear: overrideTaxYear,
        runTypeOverride: overrideRunType,
        reportType: overrideReportType,
        county,
        municipality,
        CourtDate: overrideCourtDate
      } = params;

      if (Object.keys(error).length > 0) setError({});

      const runTypeToUse = overrideRunType || runType;
      const reportTypeToUse = overrideReportType || getReportType;
      const courtDateToUse = overrideCourtDate || courtDate;

      if (runTypeToUse === 'court_date' && !uids?.length && courtDateToUse === UNASSIGNED_COURT_DATE) {
        setActiveDialog('unassignedDateSelection');
        return;
      }

      // Validate parameters
      const validationErrors = validateParameters({
        locationParams: { ...locationParams, additionalInput: additionalInput || locationParams.additionalInput },
        getReportType: reportTypeToUse,
        runType: runTypeToUse,
        courtDate: overrideCourtDate || courtDate
      });

      if (validationErrors) {
        setError(prev => ({ ...prev, ...validationErrors }));
        return;
      }

      const isTrainingUser = user?.userAccess?.level === 'Training';

      // Check for warnings
      const warningType = validateRunSizeAndWarnings({
        force,
        manualReview,
        isTrainingUser,
        getReportType: reportTypeToUse,
        runType: runTypeToUse,
        locationParams: { ...locationParams, additionalInput: additionalInput || locationParams.additionalInput },
        skipLargeQueryWarning,
        overwriteComps: overwriteOverride
      });

      if (warningType === 'manualReviewOverrideWarning') {
        showDialog('manualReviewOverrideWarning', {
          accordionId,
          force,
          overwriteComps: overwriteOverride,
          skipLargeQueryWarning,
          runType: runTypeToUse,
          propertyType,
          getReportType: reportTypeToUse,
          county: locationParams.county,
          municipality: locationParams.municipality,
          village: locationParams.village,
          TaxYear: locationParams.TaxYear,
          MuniCode: locationParams.MuniCode,
          courtDate: overrideCourtDate || courtDate,
          manualReview,
          overrideIds: additionalInput ? [additionalInput] : undefined,
        }, setActiveDialog, setLargeQueryParams);
        return;
      }

      if (warningType === 'largeQueryWarning') {
        showDialog('largeQueryWarning', {
          accordionId,
          force,
          overwriteComps: overwriteOverride,
          skipLargeQueryWarning,
          runType: runTypeToUse,
          propertyType,
          getReportType: reportTypeToUse,
          county: locationParams.county,
          municipality: locationParams.municipality,
          village: locationParams.village,
          TaxYear: locationParams.TaxYear,
          MuniCode: locationParams.MuniCode,
          courtDate: overrideCourtDate || courtDate,
          manualReview,
          overrideIds: additionalInput ? [additionalInput] : undefined,
        }, setActiveDialog, setLargeQueryParams);
        return;
      }


      setIsFetching(true);
      // Old functionality from when we were expanding the datatable as a sub-item below (to retain history of runs)
      if (accordionId) setExpandedAccordion(accordionId);

      // Build unified parameters
      const unifiedParams = buildRunParams({
        locationParams: {
          ...locationParams,
          county,
          municipality,
          village: locationParams.village,
          additionalInput: additionalInput || locationParams.additionalInput
        },
        user: user,
        overrides: {
          MuniCode: overrideMuniCode || locationParams.MuniCode,
          TaxYear: overrideTaxYear || locationParams.TaxYear,
          CourtDate: overrideCourtDate !== undefined ? overrideCourtDate : courtDate,
          reportType: reportTypeToUse,
          runType: runTypeToUse,
          uids,
          overrideIds: additionalInput ? [additionalInput] : undefined,
          UserId: user?.userId,
          // Manual review override
          overwriteComps: overwriteOverride,
          expandComps: advancedSettings.expandComps === true,
          compLimit: advancedSettings.compLimit != null && advancedSettings.compLimit !== '' ? advancedSettings.compLimit : null,
          // Advanced settings
          showSettledCases,
          manualReview,
          mlsComps: mlsCompsOnly,
          allowConditionIssues,
          scarOnly,
          reviewedCasesFlag,
          unreviewed_flag: advancedSettings?.unreviewed_flag ?? 'False',
          repID,
          // Comp filtering parameters
          squareFeetFilter: advancedSettings.squareFeetFilter,
          acreageFilter: advancedSettings.acreageFilter,
          grossAdjPctFilter: advancedSettings.grossAdjPctFilter,
          netAdjPctFilter: advancedSettings.netAdjPctFilter,
          compFilters: advancedSettings.compFilters,
          dateRange: advancedSettings.dateRange,
          dateRangeType: advancedSettings.dateRangeType,
          beforePreset: advancedSettings.beforePreset,
          afterPreset: advancedSettings.afterPreset,
          evalDate: advancedSettings.evalDate,
          Stage: advancedSettings.stage,
        },
        state: {
          propertyType,
          reportType: reportTypeToUse,
          runType: runTypeToUse,
          courtDate: overrideCourtDate || courtDate,
          badDataFlags: advancedSettings.badDataFlags
        }
      });

      // Handle settlement reports - should be unified in other new invocation?
      if (reportTypeToUse === 'settlement') {
        try {
          const settlementParams = {
            TaxYear: overrideTaxYear || locationParams.TaxYear,
            CourtDate: overrideCourtDate || courtDate,
          };
          if (runTypeToUse === 'specific_area' && (overrideMuniCode || locationParams.MuniCode)) {
            settlementParams.MuniCode = overrideMuniCode || locationParams.MuniCode;
          }
          const response = await axiosInstance.post(`/load_settlements`, settlementParams);
          const searchParams = new URLSearchParams(window.location.search);
          searchParams.set('TaxYear', (overrideTaxYear || locationParams.TaxYear).toString());
          window.history.replaceState(null, '', `${window.location.pathname}?${searchParams.toString()}`);
          setSettlementData(response.data);
          setIsFetching(false);
          return;
        } catch (error) {
          logger.error('Error fetching settlements:', error);
          setIsFetching(false);
          return;
        }
      }

      // Reset comp sheet, comps and marketing response.
      setCompSheet([]);
      setOptimizedComps([]);
      setMarketingResponse([]);

      // Fetch the UIDS and display warning dialogs
      const result = await executeUnifiedFetch(unifiedParams, skipLargeQueryWarning, uids);

      if (!result.success) {
        setIsFetching(false);
        return;
      }

      // Set the negotiation object from initializeFetch result
      const negotiationObj = result.data.negotiationObj;
      setNegotiationObj(negotiationObj);

      // Set total cases immediately for all report types
      if (result.data.uids) {
        setTotalCases(result.data.uids.length);
      }

      // Handle data analysis results if present (from initializeFetch)
      if (result.data.dataAnalysisResult) {
        setDataAnalysisResult(result.data.dataAnalysisResult);
        setCompSheet([]);
      }

      // Evidence comparison data is already stored in invokeModels
      setReviewPage(true);
      setReviewId(1);
      setShouldNavigate(true);

      // Invoke unified models function (handles all report types)
      await invokeModels({
        globalCompRef,
        compStreamObject,
        processStreamWithWorker,
        user
      });

    } catch (error) {
      logger.error('Error during analysis:', error);
      setIsFetching(false);
    }
  }, [
    locationParams,
    runType,
    getReportType,
    propertyType,
    courtDate,
    overwriteComps,
    manualReview,
    currentTaxYear,
    advancedSettings,
    error,
    user,
    setError,
    showDialog,
    setActiveDialog,
    setLargeQueryParams,
    setIsFetching,
    setExpandedAccordion,
    setCompSheet,
    setOptimizedComps,
    setMarketingResponse,
    setTotalCases,
    setReviewPage,
    setReviewId,
    setShouldNavigate,
    setSettlementData
  ]);

  // Unified fetch function that handles all the initialization logic
  const executeUnifiedFetch = async (unifiedParams, skipLargeQueryWarning, uids) => {
    // Initialize globalCompRef to prevent undefined errors
    globalCompRef.current = {
      properties: [],
      cases: [],
      totalCases: 0,
    };

    try {
      const result = await initializeFetch({
        locationParams: unifiedParams,
        user: user,
        overrides: unifiedParams, // Pass all params as overrides since they're already complete
        state: {
          propertyType,
          reportType: getReportType,
          courtDate,
          runType,
          badDataFlags
        },
        advancedSettings,
        options: {
          skipLargeQueryWarning,
          uids,
          isTownView: false
        }
      });

      if (!result.success) {
        if (result.reason === 'large_query_warning') {
          // Handle large query warning
          setActiveDialog('largeQueryWarning');
          setLargeQueryCount(result.data.count);
          // Note: We don't store params here - we'll use current locationParams state when re-running
          // Set total cases immediately for marketing table
          logger.log('setting total cases to', result.data.count)
          setTotalCases(result.data.count);
          return { success: false };
        }
        return { success: false };
      }

      // Set the negotiation object to unify our params from the initialization function
      setNegotiationObj(result.data.negotiationObj);

      // Handle data analysis results
      if (result.data.dataAnalysisResult) {
        setDataAnalysisResult(result.data.dataAnalysisResult);
        setCompSheet([]);
      }

      // Set total cases immediately for all report types
      if (result.data.uids) {
        setTotalCases(result.data.uids.length);
      }

      return { success: true, data: result.data };
      
    } catch (error) {
      logger.error('Error during fetch:', error);
      setIsFetching(false);
      toast.error(`Error: ${error.message}`, {
        position: 'top-right',
        autoClose: false,
        closeOnClick: true,
        draggable: true,
      });
      return { success: false };
    }
  };


  // Simplified runAnalysis that just calls the unified flow
  const runAnalysis = useCallback(async (params = {}) => {
    await executeAnalysis(params);
  }, [executeAnalysis]);

  const handleRunTaskFromGrid = useCallback(({ reviewType, muniCode, taxYear, uids }) => {
    logger.log('=== handleRunTaskFromGrid called ===');
    logger.log('Review Type:', reviewType);
    logger.log('Municipality Code:', muniCode);
    logger.log('Tax Year:', taxYear);
    const county = muniCode.slice(0, 1); // e.g., 'C' from 'C06'
    const municipality = muniCode.slice(1, 3); // e.g., '06' from 'C06'
    const reportType = reviewTypeToReportType[reviewType] || 'standard';
    
    logger.log('Extracted county:', county);
    logger.log('Extracted municipality:', municipality);

    // Get county and municipality strings
    const countyString = countyLookup[county] || 'Unknown County';
    const municipalityString = getLocationNameFromMuniCode(muniCode);
    logger.log('municipalityString', municipalityString)

    setLocationParams(prev => ({
      ...prev,
      MuniCode: muniCode,
      TaxYear: taxYear,
      county,
      municipality,
      countyString,
      municipalityString,
    }));
    
    // Update URL parameters to reflect the new municipality
    const urlSearchParams = new URLSearchParams(window.location.search);
    urlSearchParams.set('countyString', countyString);
    urlSearchParams.set('municipalityString', municipalityString);
    urlSearchParams.set('MuniCode', muniCode);
    urlSearchParams.set('TaxYear', taxYear);
    urlSearchParams.set('county', county);
    urlSearchParams.set('municipality', municipality);
    
    const updatedUrl = `${window.location.pathname}?${urlSearchParams.toString()}`;
    window.history.replaceState(null, '', updatedUrl);
    
    setRunType('all_clients');

    // Update URL params
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.set('MuniCode', muniCode);
    searchParams.set('TaxYear', taxYear);
    searchParams.set('county', county);
    searchParams.set('municipality', municipality);
    const newUrl = `${window.location.pathname}?${searchParams.toString()}`;
    window.history.replaceState(null, '', newUrl);

    runAnalysis({
      accordionId: 'case',
      uids,
      MuniCode: muniCode,
      TaxYear: taxYear,
      runTypeOverride: 'all_clients',
      reportType: reportType,
      county: county,
      municipality: municipality,
      // Explicitly clear state-based parameters to avoid using old values
      CourtDate: null,  
      additionalInput: '',
    });
  }, [runAnalysis]);

  // Extra filtering logic, is this same as our other from intializefetchutils?
  function filterNegotiationCases({
    rawNegotiationCases,
    updatedCases,
    advancedSettings,
    reportType,
    scarOnly,
    showSettledCases,
    uids
  }) {
    let filteredCases = rawNegotiationCases;
    let filteredUpdatedCases = updatedCases;

    // If uids are provided, filter both arrays to only include those uids
    if (uids && Array.isArray(uids) && uids.length > 0) {
      const uidsSet = new Set(uids);
      filteredCases = filteredCases.filter((c) => uidsSet.has(c.parcel_id || c.ParcelID));
      filteredUpdatedCases = filteredUpdatedCases.filter((id) => uidsSet.has(id));
    }
  
    // Filter SCAR filed cases
    if (scarOnly && reportType !== 'marketing') {
      filteredCases = filteredCases.filter((c) => c.SCARFiled === 1);
      const validParcelIds = new Set(filteredCases.map((c) => c.parcel_id));
      filteredUpdatedCases = filteredUpdatedCases.filter((id) => validParcelIds.has(id));
    }
  
    // Filter for unreviewed only
    if (advancedSettings?.unreviewed_flag !== 'False' && reportType !== 'marketing') {
      filteredCases = filteredCases.filter((c) => !c.Comps || Object.keys(c.Comps).length === 0);
      const validParcelIds = new Set(filteredCases.map((c) => c.parcel_id));
      filteredUpdatedCases = filteredUpdatedCases.filter((id) => validParcelIds.has(id));
    }
  
    // Filter for unsettled cases
    if (!showSettledCases && reportType !== 'marketing') {
      filteredCases = filteredCases.filter((c) => {
        const settled = ['S', 'SD', 'ST', 'W', 'NM', 'AH'].includes(c.SCARDeterminationAction);
        if (c.SCARFiled === 1 || c.SCARFiled === '') return !settled;
        if (c.BARFiled === 1) return !c.BARDeterminationValue || c.BARDeterminationValue === '';
        return c.BARFiled === 0 || c.BARFiled === '' || c.BARFiled === undefined;
      });
      const validParcelIds = new Set(filteredCases.map((c) => c.parcel_id));
      filteredUpdatedCases = filteredUpdatedCases.filter((id) => validParcelIds.has(id));
    }
  
    return {
      uids: filteredUpdatedCases,
      cases: filteredCases
    };
  }
  
  // use this for the manual review false saving logic, otherwise rest should disappear.
  // the streaming update to this function removed the helper function entirely. TBD if that is desired behavior or not.
  

  const handleDialogConfirm = async (action) => {
    try {
      // Handle manual review override warning dialog confirmation
      if (activeDialog === 'manualReviewOverrideWarning') {
        setActiveDialog(null);
        if (action === 'proceed') {
          // Use current locationParams state to ensure fresh data
          const params = {
            accordionId: 'case',
            force: true,
            overwriteComps: true,
            skipLargeQueryWarning: true, // Skip warnings on re-run
            // Use current state for location params
            MuniCode: locationParams.MuniCode,
            TaxYear: locationParams.TaxYear,
            county: locationParams.county,
            municipality: locationParams.municipality,
            village: locationParams.village,
            additionalInput: locationParams.additionalInput,
            runTypeOverride: runType,
            reportType: getReportType,
            CourtDate: courtDate
          };
          
          setTimeout(() => {
            runAnalysis(params);
          }, 0);
        }
        return;
      }

      // Handle large query warning dialog confirmation
      if (activeDialog === 'largeQueryWarning') {
        setActiveDialog(null);
        // Use current locationParams state instead of stored params to ensure fresh data
        const params = {
          accordionId: 'case',
          force: true,
          skipLargeQueryWarning: true,
          // Use current state for location params
          MuniCode: locationParams.MuniCode,
          TaxYear: locationParams.TaxYear,
          county: locationParams.county,
          municipality: locationParams.municipality,
          village: locationParams.village,
          additionalInput: locationParams.additionalInput,
          runTypeOverride: runType,
          reportType: getReportType,
          CourtDate: courtDate
        };
        
        setTimeout(() => {
          runAnalysis(params);
        }, 0);
      }
    } catch (error) {
      logger.error('Error in handleDialogConfirm:', error);
      toast.error('An error occurred while processing the task');
    }
  };

  // const handleConfirmationClose = () => setConfirmationOpen(false);
  

  const loadUnassignedCourtCases = useCallback(async () => {
    setUnassignedLoading(true);
    try {
      const response = await axiosInstance.get('/load_unassigned_court_cases');
      setUnassignedOptions(response.data?.options || []);
    } catch (err) {
      logger.error('Error loading unassigned court cases:', err);
      toast.error('Failed to load unassigned dates.');
      setUnassignedOptions([]);
    } finally {
      setUnassignedLoading(false);
    }
  }, []);

  useEffect(() => {
    if (courtDate === UNASSIGNED_COURT_DATE) loadUnassignedCourtCases();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Need to update this to return the muni string form this backend route to complete this flow fully.
  const handleSetCourtDateOptions = async (courtDate) => {
    setCourtDate(prev => prev !== courtDate ? courtDate : prev);
    
    // Update URL with court date
    const searchParams = new URLSearchParams(window.location.search);
    if (courtDate) {
      searchParams.set('courtDate', courtDate);
    } else {
      searchParams.delete('courtDate');
      if (restrictedYear !== null) {
        setRestrictedYear(null); // Clear restricted year when court date is cleared
      }
    }
    
    // Update URL
    const newUrl = `${window.location.pathname}?${searchParams.toString()}`;
    window.history.replaceState(null, '', newUrl);

    if (courtDate === UNASSIGNED_COURT_DATE) {
      if (restrictedYear !== null) setRestrictedYear(null);
      setSelectedCourtDate(null);
      setCourtDateOptions([]);
      setSelectedUnassignedKey('');
      setActiveDialog('unassignedDateSelection');
      loadUnassignedCourtCases();
      return;
    }

    // Find the selected court date from the already-loaded court dates
    const selectedCourtDateObj = courtDates.find(cd => cd.value === courtDate);
    
    const muniOptions = selectedCourtDateObj?.MuniOptions || [];
    // Only update if the array reference or content has changed
    if (!isEqual(courtDateOptions, muniOptions)) {
      setCourtDateOptions(muniOptions);
    }

      // If there's exactly one option, automatically set the restricted year
      if (muniOptions.length === 1) {
        const county = muniOptions[0].muni.slice(0, 1);
        const municipality = muniOptions[0].muni.slice(1, 3);
        const countyString = countyLookup[county] || 'Unknown County';
        const muniCode = muniOptions[0].muni;
        const municipalityString = getLocationNameFromMuniCode(muniCode);
        const TaxYear = muniOptions[0].TaxYear;
        
        setRestrictedYear(TaxYear); // Set the restricted year
        
        const updates = { county, municipality, MuniCode: muniCode, countyString, municipalityString, TaxYear };
        Object.entries(updates).forEach(([key, value]) => {
          searchParams.set(key, value);
        });
      
        const newUrl = `${window.location.pathname}?${searchParams.toString()}`;
        window.history.replaceState(null, '', newUrl);

        updateLocationParams(updates);
      } else if (muniOptions.length > 1) {
        setActiveDialog('courtDateSelection');
      }
  }

  const showHistoricalCourtdates =
    advancedSettings?.showSettledCourtdates || user?.viewHistorical || false;

  const orderedCourtDates = useMemo(() => {
    const list = courtDates || [];
    const ordered = showHistoricalCourtdates ? list.slice().reverse() : list;
    return [{ label: 'Unassigned Date', value: UNASSIGNED_COURT_DATE }, ...ordered];
  }, [courtDates, showHistoricalCourtdates]);

  const unassignedMenuOptions = unassignedOptions.map((option) => {
    const name = getLocationNameFromMuniCode(option.muni);
    return {
      key: `${option.muni}|${option.TaxYear}`,
      muni: option.muni,
      TaxYear: option.TaxYear,
      uids: option.uids || [],
      label: `${name} - (${option.muni}) (${option.TaxYear})`,
    };
  });

  const applyUnassignedSelection = (option) => {
    const county = option.muni.slice(0, 1);
    const municipality = option.muni.slice(1, 3);
    setRestrictedYear(option.TaxYear);
    updateLocationParams({
      county,
      municipality,
      MuniCode: option.muni,
      TaxYear: option.TaxYear,
      countyString: countyLookup[county] || 'Unknown County',
      municipalityString: getLocationNameFromMuniCode(option.muni),
      village: '',
      villageString: '',
    });
  };

  const handleAdditionalInputChange = useCallback((input) => {
    if (input?.length > 5) {
      const county = input.slice(0, 1);
      const municipality = input.slice(1, 3);
      const muniCode = input.slice(0, 3);
      
      const countyObj = counties.find(c => c.selectVal === county);
      const countyString = countyObj?.key || 'Unknown County';

      const municipalityList = getMunicipalities(countyString) || [];
      const muniObj = municipalityList.find(m => m.selectVal === municipality);
      const municipalityString = muniObj?.key || 'Unknown Municipality';

      updateLocationParams({
        county,
        municipality,
        MuniCode: muniCode,
        countyString,
        municipalityString,
        additionalInput: input,
        village: '',
        villageString: '',
      });
    } else {
      updateLocationParams({ additionalInput: input });
    }
  }, [updateLocationParams, counties, getMunicipalities]);

  // Modify the report type onChange handler
  const handleReportTypeChange = (event) => {
    const newReportType = event.target.value;
    logger.log(`🔄 [REPORT_TYPE_CHANGE] Changing report type to: ${newReportType}`);
    
    setReportType(newReportType);
    
    const updates = { reportType: newReportType };
    
    logger.log('newReportType', newReportType)
    if (newReportType === 'settlement' || newReportType === 'marketing') {
      logger.log('running this to reset old state')
      const newRunType = 'all_clients';
      setRunType(newRunType);
      updates.runType = newRunType;
      setCourtDate(null);
      setSelectedCourtDate(null);
      setCourtDateOptions([]);
      // Also clear these from URL
      updates.courtDate = null;
      updates.additionalInput = '';
      // Clear restricted year when changing to these report types
      setRestrictedYear(null);
    }

    // Rep ID run is allowed for sales/assessment/evidence comparison/data analysis
    const repIdAllowedReports = new Set(['standard', 'assessment', 'evidence_comparison', 'data_analysis']);
    const isRepIdRunDisallowed = runType === 'rep_id' && !repIdAllowedReports.has(newReportType);
    if (isRepIdRunDisallowed) {
      const newRunType = 'all_clients';
      setRunType(newRunType);
      updates.runType = newRunType;
      if (repID !== '') setRepID('');
      updates.repID = null;
    }
    
    if (newReportType === 'settlement') {
      updates.county = 'All';
      updates.municipality = 'All';
      updates.MuniCode = 'All';
    }
    
    // Update URL params
    const searchParams = new URLSearchParams(window.location.search);
    Object.entries(updates).forEach(([key, value]) => {
      if (value) {
        searchParams.set(key, value);
      } else {
        searchParams.delete(key);
      }
    });
    
    // Update URL without triggering navigation
    const newUrl = `${window.location.pathname}?${searchParams.toString()}`;
    window.history.replaceState(null, '', newUrl);
    
    updateLocationParams(updates);
    logger.log(`✅ [REPORT_TYPE_CHANGE] Completed report type change to: ${newReportType}`);
  };

  // Add state for tool type
  const [toolType, setToolType] = useState('case-analysis');
  const [filingType, setFilingType] = useState('BAR');
  // Add state to control CompSearch input value
  const [compSearchValue, setCompSearchValue] = useState(() => {
    // Initialize with existing additionalInput if available
    return locationParams.additionalInput || "";
  });



  const handleCompSelect = useCallback(
    ({ parcel_id, suggestion }) => {
      // Update the display value for the CompSearch component
      setCompSearchValue(suggestion || parcel_id || "");
      // Update the location params for the actual analysis
      handleAdditionalInputChange(parcel_id);
      // Don't automatically run analysis - let user manually trigger it
    },
    [handleAdditionalInputChange]
  );

  // Define the width (max) of the thing you are passing in.
  // Need to make court date selection smaller.
  const fields = [
    {
      type: 'select',
      label: 'Tool',
      value: toolType,
      width: toolType === 'filing-generation' ? '170px' : '150px',
      minWidth: toolType === 'filing-generation' ? '170px' : '150px',
      onChange: (event) => {
        setToolType(event.target.value);
      },
      options: [
        { label: 'Case Analysis', value: 'case-analysis' },
        { label: 'Filing Generation', value: 'filing-generation' },
      ],
    },
    {
      type: 'select',
      label: 'Property Type',
      value: propertyType,
      width: '140px',
      onChange: (event) => setPropertyType(event.target.value),
      options: [
        { label: 'Residential', value: 'residential' },
        { label: 'Commercial', value: 'commercial', disabled: true },
        { label: 'Industrial', value: 'industrial', disabled: true },
      ],
    },
      ...(toolType === 'filing-generation'
        ? [
            {
              type: 'select',
              label: 'Filing Type',
              value: filingType,
              width: '140px',
              onChange: (event) => setFilingType(event.target.value),
              options: [
                { label: 'BAR', value: 'BAR' },
                { label: 'SCAR', value: 'SCAR', disabled: true, tooltip: 'SCAR filing is not available yet' },
              ],
            },
          ]
        : [
      {
        type: 'select',
        label: 'Report',
        value: getReportType,
        width: '140px',
        onChange: handleReportTypeChange,
        options: (() => {
          const hasVillage = village && village !== 'All' && village !== '';
          const all = [
            { label: 'Sales Analysis', value: 'standard' },
            { 
              label: 'Assessment Analysis', 
              value: 'assessment',
              disabled: hasVillage,
              tooltip: hasVillage ? 'Villages cannot be run with Assessment Analysis' : undefined,
            },
            { 
              label: 'Evidence Comparison', 
              value: 'evidence_comparison',
              disabled: hasVillage,
              tooltip: hasVillage ? 'Villages cannot be run with Evidence Comparison' : undefined,
            },
            
            // Removed this flow, going to delete the old code for settlement analysis.
            // negotiator analysis rebuilt is the new flow. Need to bundle in the cashflow / invoicing stuff + fiscal to make this swap.
            // { label: 'Settlement Analysis', value: 'settlement' },
            { label: 'Marketing Analysis', value: 'marketing' },
            { 
              label: 'Data Analysis', 
              value: 'data_analysis',
              disabled: hasVillage,
              tooltip: hasVillage ? 'Villages cannot be run with Data Analysis' : undefined,
            },
          ];
          return all.filter((opt) => canRunReport(opt.value));
        })(),
      },
      {
        type: 'select',
        label: 'Run Type',
        value: locationParams.runType,
        onChange: handleRunTypeChange,
        width: '140px',
        disabled: (() => {
          // const isDisabled = getReportType !== 'standard' && getReportType !== 'assessment' && getReportType !== 'data_analysis' && getReportType !== 'marketing';
          const isDisabled = getReportType === 'settlement'
          // || getReportType === 'evidence_comparison'
          ;
          return isDisabled;
        })(),
        options: (() => {
          const hasVillage = village && village !== 'All' && village !== '';
          const restrictedRunTypes = ['court_date'];
          const incompatibleReportTypes = ['settlement', 'marketing'];
          const repIdAllowedReports = new Set(['standard', 'assessment', 'evidence_comparison', 'data_analysis']);
          
          const base = [
            { label: 'All Clients', value: 'all_clients' },
            { 
              label: 'Court Date', 
              value: 'court_date',
              tooltip: hasVillage ? 'Villages cannot be run with courtdates' : undefined,
            },
            { 
              label: 'Single Parcel', 
              value: 'single_parcel',
              tooltip: hasVillage ? 'Villages cannot be run with single parcels' : undefined,
            },
            { label: 'Rep ID', value: 'rep_id' },
          ];
          
          return base.map((opt) => {
            const isUserRestricted = !canUseRunType(opt.value);

            // Force to run all clients if:
            const isRestrictedByReportType = restrictedRunTypes.includes(opt.value) && incompatibleReportTypes.includes(getReportType);
            const isRestrictedByVillage = hasVillage && restrictedRunTypes.includes(opt.value);
            const isRestrictedByRepIdReport =
              opt.value === 'rep_id' && !repIdAllowedReports.has(getReportType);
            
            return {
              ...opt,
              disabled: isUserRestricted || isRestrictedByReportType || isRestrictedByVillage || isRestrictedByRepIdReport,
            };
          });
        })(),
      },
      ...(runType === 'court_date' || (getReportType === 'data_analysis' && runType === 'court_date')
        ? [
            {
              type: 'select',
              label: 'Court Date',
              value: courtDate,
              onChange: (event) => {
                if (error.courtDate) {
                  setError((prev) => {
                    if (prev.courtDate === null) return prev;
                    return { ...prev, courtDate: null };
                  });
                }
                handleSetCourtDateOptions(event.target.value);
              },
              options: orderedCourtDates,
              error: error.courtDate,
              width: '140px',
            },
          ]
        : []),

      // Conditionally show either additional input or bad data flags based on report type
      ...(getReportType === 'data_analysis' 
        ? [
            // Show bad data fields for data analysis
            {
              type: 'custom',
              label: 'Bad Data Fields',
              width: '300px',
              component: (
                <BadDataFlagsSelector
                  uniqueFields={flagType === 'data' ? AVAILABLE_BAD_DATA_FIELDS : ['Sqft', 'Acres', 'YearBuilt', 'Bath', 'SalePrice', 'Style', 'TotalGarageCap']}
                  dataSelectedCount={badDataFlags[flagType]?.length || 0}
                  fieldDisplayName={(field) => columnHeaderMappings[field] || field}
                />
              ),
            },
          ] : []),
      // Conditionally show CompSearch or Rep ID input for single_parcel run types
      ...((runType === 'single_parcel')
        ? [
            runType === 'single_parcel'
              ? {
                  type: 'custom',
                  label: 'Parcel ID or Address',
                  width: '320px',
                  component: (
                    <CompSearch
                      value={compSearchValue}
                      onChange={setCompSearchValue}
                      compSelectCallback={handleCompSelect}
                      placeholderText="Enter Address or Parcel ID"
                      clearOnSelect={false}
                    />
                  ),
                  error: error.additionalInput,
                }
              : {
                  type: 'text',
                  label: 'Rep ID',
                  value: locationParams.additionalInput,
                  width: '140px',
                  error: error.additionalInput,
                  onChange: (event) => {
                    if (error.additionalInput) {
                      setError((prev) => ({ ...prev, additionalInput: null }));
                    }
                    const trimmedValue = event.target.value.trimStart();
                    handleAdditionalInputChange(trimmedValue);
                  },
                },
          ] : []),

      ...(runType === 'rep_id'
        ? [
            {
              type: 'custom',
              label: 'Rep ID',
              width: '140px',
              error: error.repID,
              component: (
                <Box sx={{ width: '100%' }}>
                  <TextField
                    size="small"
                    label="Rep ID"
                    value={repID}
                    name="repID"
                    autoComplete="off"
                    onChange={(event) => {
                      if (error.repID) setError((prev) => ({ ...prev, repID: null }));
                      const next = String(event.target.value ?? '').replace(/[^\d]/g, '');
                      setRepID(next);
                      const searchParams = new URLSearchParams(window.location.search);
                      if (next) searchParams.set('repID', next);
                      else searchParams.delete('repID');
                      window.history.replaceState(null, '', `${window.location.pathname}?${searchParams.toString()}`);
                    }}
                    inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                    error={!!error.repID}
                    sx={{ width: '100%' }}
                  />
                </Box>
              ),
            },
          ]
        : []),
      ]),
  ];

  const analysisButtons = [
    {
      type: 'tooltip',
      tooltip: (() => {
        const activeFilters = [];
        
        // Check for non-default run types (excluding 'settled' which is default)
        const nonDefaultRunTypes = advancedSettings?.defaultRunTypes?.filter(type => type !== 'settled') || [];
        if (nonDefaultRunTypes.includes('scarFiled')) {
          activeFilters.push('SCAR Filed cases only');
        }
        if (advancedSettings?.unreviewed_flag !== 'False') {
          activeFilters.push('Unreviewed cases only');
        }
        
        // Check if autosave is off (differs from default true)
        if (advancedSettings?.autosave === false) {
          activeFilters.push('Autosave is OFF');
        }
        
        // Check if currentFilter is set and not "Default"
        if (advancedSettings?.currentFilter && advancedSettings.currentFilter !== '' && advancedSettings.currentFilter !== 'Default') {
          activeFilters.push(`Custom Filter: ${advancedSettings.currentFilter}`);
        }
        
        // Check if comp limit is explicitly set (only when autosave is enabled and not the default for report type)
        if (advancedSettings?.autosave === true && isCompLimitExplicitlySet(advancedSettings?.compLimit, getReportType)) {
          activeFilters.push(`Save ${advancedSettings.compLimit} Comps`);
        }
        
        // Check if manual review override is enabled
        if (advancedSettings?.manualReviewOverride === 1) {
          activeFilters.push('Set to overwrite manually reviewed comps');
        }

        if (advancedSettings?.highTaxRun) {
          const n = Number(advancedSettings?.highTaxCount);
          activeFilters.push(
            Number.isFinite(n) && n >= 1
              ? `High tax run enabled (count: ${Math.floor(n)})`
              : 'High tax run enabled'
          );
        }

        {
          const months = Math.floor(Number(advancedSettings?.timeAdjustmentMonths));
          if (Number.isFinite(months) && months >= 1) {
            activeFilters.push(`Time adjusted price enabled (months: ${months})`);
          }
        }

        if (advancedSettings?.stage === 'BAR') {
          activeFilters.push('Algorithm run type is set to BAR');
        }
        if (
          typeof advancedSettings?.stage === 'string' &&
          advancedSettings.stage.toLowerCase() === 'marketing'
        ) {
          activeFilters.push('Algorithm run type is set to Marketing');
        }
        
        return activeFilters.length > 0 
          ? <div style={{ whiteSpace: 'pre-line' }}>{activeFilters.join('\n')}</div>
          : '';
      })(),
      children: <InfoIcon sx={{ 
        color: (hasOverrideEnabled || hasHighTaxRun || hasTimeAdjustmentEnabled || hasAlgorithmStageWarning) ? 'error.main' : 'warning.main',
        visibility: (hasNonDefaultFilters || hasOverrideEnabled || hasHighTaxRun || hasTimeAdjustmentEnabled || hasAlgorithmStageWarning) ? 'visible' : 'hidden',
        mr: 1
      }} />,
    },
    ...(mlsCompsOnly
      ? [
          {
            type: 'tooltip',
            tooltip:
              'MLS-only comparables filter is on. It is recommended to NOT run with this filter.',
            children: (
              <ReportProblemOutlinedIcon
                sx={{
                  color: 'warning.dark',
                  mr: 1,
                  fontSize: '1.35rem',
                }}
              />
            ),
          },
        ]
      : []),
    {
      type: 'icon',
      tooltip: 'Advanced Settings',
      onClick: () => handleActiveDialog('settings'),
    },
    ...(toolType === 'case-analysis' ? [
      {
        type: 'button',
        variant: 'contained',
        color: 'primary',
        label: 'Run',
        onClick: () => {
          if (courtDate === UNASSIGNED_COURT_DATE) {
            setActiveDialog('unassignedDateSelection');
            if (!unassignedLoading && unassignedOptions.length === 0) loadUnassignedCourtCases();
            return;
          }
          runAnalysis({
            accordionId: 'case',
            additionalInput: locationParams.additionalInput
          });
        },
        disabled: getIsFetching,
        endIcon: getIsFetching ? <CircularProgress size={16} sx={{
          color: 'white',
        }}/> : null,
      },
    ] : toolType === 'filing-generation' ? [
      {
        type: 'button',
        variant: 'contained',
        color: 'primary',
        label: 'Run',
        onClick: () => {
          const validationErrors = validateParameters({
            locationParams,
            getReportType: 'standard',
            runType: 'all_clients',
            courtDate: null,
          });
          if (validationErrors) {
            setError((prev) => ({ ...prev, ...validationErrors }));
            return;
          }
          invokeFilingGeneration({
            filingType,
            locationParams,
            propertyType,
            user,
            advancedSettings,
          });
        },
        disabled: getIsFetching,
        endIcon: getIsFetching ? <CircularProgress size={16} sx={{ color: 'white' }} /> : null,
      },
    ] : []),
  ];

  // make sure you keep a stable ref for this & memoize the rows
  const dataGridProps = useMemo(
    () => ({
      columns: [
        { field: 'id', headerName: 'ID', width: 90 },
        {
          field: 'name',
          headerName: 'Name',
          width: 150,
          renderCell: (params) => (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center', // Vertically center content
                gap: 1,
                height: '100%', // Ensures proper alignment in the cell
                overflow: 'hidden', // Prevents overflow issues
              }}
            >
              <Typography
                sx={{
                  color: 'blue',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                }}
                onClick={() => {
                  // here, call the 
                  // setReviewId(params.id)
                  setReviewPage(true);
                  setReviewId(1)
                }
                }
              >
                {params.value}
              </Typography>
              {getIsFetching && <CircularProgress size={16} />}
            </Box>
          ),
        },
        { field: 'value', headerName: 'Value', width: 150 },
      ],
      height: 250,
      rows: dataGridRows,
      localeText: { noRowsLabel: 'Run Analysis to see results' },
      pageSizeOptions: [5, 10, 20],
      disableColumnMenu: true,
      disableSelectionOnClick: true,
    }),
    [getIsFetching, dataGridRows, setReviewPage, setReviewId]
  );

  // Here begins the retarded old logic:
  // refactored the initialization, removed adjustment load.



  return  (
    <div className='w-full h-full flex flex-col'>
      {shouldNavigate && (
        <Navigate to={`/analysis/1${window.location.search}`} replace />
      )}
      <Container
        maxWidth="xl"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          py: 1,
          flex: 1,
          minHeight: 0,
        }}
      >

        {/* The muni-multiselect (sets params) */}
        <MuniSelectionV2
          error={error}
          handleClearError={clearError}
          locationParams={locationParams}
          updateLocationParams={updateLocationParams}
          restrictedYear={restrictedYear}
        />

        {shareWorkups && (
          <Typography variant="caption" color="text.secondary" sx={{ px: 0.5 }}>
            This town access link includes workup sharing.
            {user?.showOnePagers === false
              ? ' One-pager workups are not enabled for this link.'
              : ''}
          </Typography>
        )}

        {/* move these cards to their own component */}
        {/* top display cards (most important stats for area): */}
      
        {/* Case Analysis container (finally, lol) */}
        <AnalysisAccordion
          title="User Tools"
          fields={fields}
          buttons={analysisButtons}
          dataGridProps={dataGridProps}
          expanded={expandedAccordion === 'case'}
          onExpandToggle={() =>
            setExpandedAccordion(expandedAccordion === 'case' ? null : 'case')
          }
        >
        </AnalysisAccordion>

        <StableMuiDialog open={!!activeDialog} onClose={handleDialogClose}>

            {activeDialog === 'manualReviewOverrideWarning' && (
              <>
                <DialogTitle sx={{ color: 'error.main', fontWeight: 'bold' }}>
                  ⚠️ Override Manually Reviewed Comps
                  <IconButton
                    aria-label="close"
                    onClick={handleDialogClose}
                    sx={{
                      position: 'absolute',
                      right: 8,
                      top: 8,
                      color: (theme) => theme.palette.grey[500],
                    }}
                  >
                    <CloseIcon />
                  </IconButton>
                </DialogTitle>
                <DialogContent>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    You are about to run analysis with manual review override enabled. This means:
                  </Typography>
                  <Typography variant="body2" component="div" sx={{ pl: 2 }}>
                    • <strong>Existing manually reviewed comps will be overwritten</strong> when running this analysis
                    <br />
                    • This action cannot be easily undone
                    <br />
                    • Make sure you understand the implications before proceeding
                  </Typography>
                </DialogContent>
                <DialogActions>
                  <Button onClick={handleDialogClose} color="primary">
                    Cancel
                  </Button>
                  <Button 
                    onClick={() => handleDialogConfirm('proceed')} 
                    color="error"
                    variant="contained"
                  >
                    Proceed with Override
                  </Button>
                </DialogActions>
              </>
            )}

            {activeDialog === 'largeQueryWarning' && (
              <>
                <DialogTitle>Warning
                  <IconButton
                    aria-label="close"
                    onClick={handleDialogClose}
                    sx={{
                      position: 'absolute',
                      right: 8,
                      top: 8,
                      color: (theme) => theme.palette.grey[500],
                    }}
                  >
                    <CloseIcon />
                  </IconButton>
                </DialogTitle>
                <DialogContent>
                  <Typography>
                    There are {largeQueryCount || 'many'} {getReportType === 'marketing' ? 'parcels' : 'clients'}. This will take a little while to run. Would you like to proceed?
                  </Typography>
                </DialogContent>
                <DialogActions>
                  <Button onClick={handleDialogClose}>Cancel</Button>
                  <Button onClick={() => handleDialogConfirm('proceed')} variant="contained" color="primary">
                    Proceed
                  </Button>
                </DialogActions>
              </>
            )}

                {/* Multicourt date selection dialog */}
            {activeDialog === 'courtDateSelection' && (
              <>
                <DialogTitle>Municipality Selection
                  <IconButton
                    aria-label="close"
                    onClick={handleDialogClose}
                    sx={{
                      position: 'absolute',
                      right: 8,
                      top: 8,
                      color: (theme) => theme.palette.grey[500],
                    }}
                  >
                    <CloseIcon />
                  </IconButton>
                </DialogTitle>
                <DialogContent>
                  <Typography variant="body2" gutterBottom>
                    Please select which municipality you would like to run.
                  </Typography>
                  <Select
                    value={selectedCourtDate || ''}
                    onChange={(event) => {
                      const selectedMuni = event.target.value;
                      setSelectedCourtDate(selectedMuni);
                      
                      // Find the matching court date option
                      const selectedOption = courtDateOptions.find(option => option.muni === selectedMuni);
                      
                      if (selectedOption) {
                        const county = selectedOption.muni.slice(0, 1);
                        const municipality = selectedOption.muni.slice(1, 3);
                        
                        // Get the county name from lookup
                        const countyObj = counties.find(c => c.selectVal === county);
                        const countyString = countyObj?.key || 'Unknown County';
                        
                        // Get municipality name from lookup
                        const municipalityList = getMunicipalities(countyString) || [];
                        const muniObj = municipalityList.find(m => m.selectVal === municipality);
                        const municipalityString = muniObj?.key || 'Unknown Municipality';
                        
                        // Set the restricted year
                        setRestrictedYear(selectedOption.TaxYear);
                        
                        // Update URL params
                        const searchParams = new URLSearchParams(window.location.search);
                        const updates = {
                          county,
                          municipality,
                          MuniCode: selectedOption.muni,
                          TaxYear: selectedOption.TaxYear,
                          countyString,
                          municipalityString,
                          village: '',
                          villageString: '',
                        };
                        
                        Object.entries(updates).forEach(([key, value]) => {
                          if (value) {
                            searchParams.set(key, value);
                          } else {
                            searchParams.delete(key);
                          }
                        });
                        
                        // Update URL
                        const newUrl = `${window.location.pathname}?${searchParams.toString()}`;
                        window.history.replaceState(null, '', newUrl);
                        
                        // Update location params
                        updateLocationParams(updates);
                      }
                    }}
                    displayEmpty
                    fullWidth
                  >
                    <MenuItem value="" disabled>
                      Select a Municipality
                    </MenuItem>
                    {courtDateOptions.map((option, index) => {
                      const county = option.muni.slice(0, 1);
                      const municipality = option.muni.slice(1, 3);
                      
                      // Get the county name from lookup
                      const countyObj = counties.find(c => c.selectVal === county);
                      const countyString = countyObj?.key || 'Unknown County';
                      
                      // Get municipality name from lookup
                      const municipalityList = getMunicipalities(countyString) || [];
                      const muniObj = municipalityList.find(m => m.selectVal === municipality);
                      const municipalityString = muniObj?.key || 'Unknown Municipality';

                      return (
                        <MenuItem key={index} value={option.muni}>
                          {`${municipalityString} - (${option.muni})`}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </DialogContent>
                <DialogActions>
                  <Button onClick={handleDialogClose}>Close</Button>
                  <Button
                    onClick={() => {
                      const selectedOption = courtDateOptions.find(option => option.muni === selectedCourtDate);
                      if (selectedOption) {
                        const county = selectedOption.muni.slice(0, 1);
                        const municipality = selectedOption.muni.slice(1, 3);
                        handleDialogClose();
                        // Use selected values explicitly to ensure fresh data
                        runAnalysis({
                          accordionId: 'case',
                          force: true,
                          MuniCode: selectedOption.muni,
                          TaxYear: selectedOption.TaxYear,
                          county: county,
                          municipality: municipality,
                          CourtDate: courtDate
                        });
                      }
                    }}
                    variant="contained"
                    color="primary"
                    disabled={!selectedCourtDate || selectedCourtDate === ''}
                  >
                    Run
                  </Button>
                </DialogActions>
              </>
            )}

            {activeDialog === 'unassignedDateSelection' && (
              <>
                <DialogTitle>Municipality Selection
                  <IconButton
                    aria-label="close"
                    onClick={handleDialogClose}
                    sx={{
                      position: 'absolute',
                      right: 8,
                      top: 8,
                      color: (theme) => theme.palette.grey[500],
                    }}
                  >
                    <CloseIcon />
                  </IconButton>
                </DialogTitle>
                <DialogContent>
                  <Typography variant="body2" gutterBottom>
                    Please select which municipality you would like to run.
                  </Typography>
                  {unassignedLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : unassignedMenuOptions.length === 0 ? (
                    <Typography variant="body2">No unassigned dates found.</Typography>
                  ) : (
                    <Select
                      value={selectedUnassignedKey}
                      onChange={(event) => {
                        const selected = unassignedMenuOptions.find((option) => option.key === event.target.value);
                        setSelectedUnassignedKey(event.target.value);
                        if (selected) applyUnassignedSelection(selected);
                      }}
                      displayEmpty
                      fullWidth
                    >
                      <MenuItem value="" disabled>
                        Select a Municipality
                      </MenuItem>
                      {unassignedMenuOptions.map((option) => (
                        <MenuItem key={option.key} value={option.key}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                </DialogContent>
                <DialogActions>
                  <Button onClick={handleDialogClose}>Close</Button>
                  <Button
                    onClick={() => {
                      const selected = unassignedMenuOptions.find((option) => option.key === selectedUnassignedKey);
                      if (!selected) return;
                      applyUnassignedSelection(selected);
                      handleDialogClose();
                      runAnalysis({
                        accordionId: 'case',
                        force: true,
                        uids: selected.uids,
                        MuniCode: selected.muni,
                        TaxYear: selected.TaxYear,
                        county: selected.muni.slice(0, 1),
                        municipality: selected.muni.slice(1, 3),
                        CourtDate: null,
                      });
                    }}
                    variant="contained"
                    color="primary"
                    disabled={!selectedUnassignedKey || unassignedLoading}
                  >
                    Run
                  </Button>
                </DialogActions>
              </>
            )}

        </StableMuiDialog>

        {/* This flow has gotten so stupid, it currently renders when not open. */}
        <AdvancedSettings
          open={activeDialog === 'settings'}
          onClose={handleDialogClose}
          onConfirm={handleSettingsConfirm}
          initialSettings={advancedSettings}
          runAnalysis={runAnalysis}
          adjustments={getAdjustments}
          onSaveAdjustments={handleSaveAdjustments}
          filters={filters}
          isLoadingFilters={isLoadingFilters}
          fetchFilters={fetchFilters}
          currentFilter={currentFilter}
        />

        {/* Isolated from CaseAnalysisContainer rerenders (negotiationObj/adjustments/isFetching). */}
        <AnalysisBottomPanel
          activeUser={user?.userId}
          isAdmin={isAdmin}
          canCreateTasks={canCreateTasks}
        />
    </Container>
    </div>
  );
}

if (process.env.NODE_ENV === 'development') {
  CaseAnalysisContainer.whyDidYouRender = true;
}

export default CaseAnalysisContainer;